import React, { useState } from "react";

import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { BrandContext } from "../../App";
import { useAuth } from "../../contexts/AuthContext";
import { useUI } from "../../contexts/UIContext";
import { useAPI } from "../../hooks/useAPI";
import HomeProgramCard from "../../components/complex/HomeProgramCard";
import PreviousResultsChart from "../../components/complex/PreviousResultsChart";
import Metrics from "./Widgets/Metrics/Metrics";
import SidebarWidget from "./Widgets/SidebarWidget/SidebarWidget";
import CIWidget from "./Widgets/CIWidget/CIWidget.js";

import CircularProgress from "@mui/material/CircularProgress";
import VideoModal from "../VideoModal/VideoModal";

import PageTitle from "../../components/PageTitle/PageTitle";
import DefaultButton from "../../components/simple/DefaultButton";

import { Container } from "./styles.js";

const DashLoading = ({ text = "Loading..." }) => (
  <div className="dash-loader">
    <CircularProgress enableTrackSlot />
    <span>{text}</span>
  </div>
);

const METRIC_CONFIG = {
  day_streak: {
    name: "Day Streak",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_45_824)">
          <path
            d="M11.0868 1.93342C10.5622 1.48876 10.0121 1.02142 9.45215 0.512073C9.02256 0.121386 8.44237 -0.059677 7.86681 0.0174168C7.30906 0.0911043 6.81281 0.408573 6.51215 0.884073C5.71746 2.21207 5.15559 3.6661 4.85081 5.18342C4.73121 5.01073 4.62512 4.8291 4.53346 4.64007C4.21771 3.97482 3.42246 3.69148 2.75721 4.00723C2.59615 4.08367 2.45206 4.19164 2.33346 4.32473C1.21524 5.46742 0.595337 7.00664 0.609462 8.60539C0.577681 11.9658 2.83802 14.9161 6.09081 15.7601C6.69602 15.9124 7.3174 15.9911 7.94146 15.9947C7.96146 15.9947 8.17412 15.9927 8.26012 15.9867C12.2401 15.8578 15.3984 12.5915 15.3935 8.60939C15.3908 5.58942 13.3962 3.89607 11.0868 1.93342ZM7.92481 14.6567C7.42865 14.619 6.93834 14.418 6.56815 14.1194C5.98846 13.7092 5.60487 13.1077 5.51349 12.4147C5.40015 11.3334 6.06684 9.91939 7.42884 8.32207C7.57165 8.15526 7.78059 8.05967 8.00018 8.06073C8.21693 8.05917 8.42306 8.15451 8.56218 8.32073C9.81018 9.80207 10.4955 11.1687 10.4955 12.1694C10.4917 13.4841 9.46409 14.5241 8.15552 14.6507C8.09805 14.6563 8.00015 14.6647 7.92481 14.6567ZM11.7175 13.3887C11.6781 13.4194 11.6348 13.4441 11.5948 13.4734C11.7486 13.0553 11.8279 12.6135 11.8288 12.168C11.8288 10.4834 10.6055 8.67804 9.58018 7.46004C9.1879 6.99545 8.6109 6.7272 8.00284 6.7267H8.00015C7.39087 6.72561 6.81177 6.99201 6.41615 7.45536C4.78615 9.3647 4.03749 11.08 4.19081 12.554C4.22721 12.8962 4.31462 13.231 4.45015 13.5474C2.86709 12.4003 1.93431 10.5603 1.94481 8.60536C1.93193 7.33539 2.43274 6.11407 3.33346 5.2187C3.48724 5.53839 3.6719 5.84226 3.88481 6.12604C4.17721 6.52136 4.67674 6.70479 5.15546 6.5927C5.64356 6.48473 6.02252 6.09976 6.12281 5.61004C6.39065 4.19429 6.90249 2.83585 7.63546 1.59539C7.72565 1.45357 7.87462 1.35964 8.04146 1.33939C8.22793 1.31429 8.4159 1.37314 8.55481 1.50004C9.12681 2.02004 9.68815 2.50004 10.2215 2.95139C12.4468 4.84139 14.0555 6.20673 14.0555 8.60939C14.0612 10.4792 13.1981 12.2456 11.7195 13.39L11.7175 13.3887Z"
            fill="#FDB022"
          />
        </g>
        <defs>
          <clipPath id="clip0_45_824">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    format: (v) => `${v} days`,
  },
  sessions_completed: {
    name: "Sessions Completed",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_37_865)">
          <path
            d="M7.99992 14L7.93322 13.8999C7.47012 13.2053 7.23857 12.858 6.93265 12.6065C6.66182 12.3839 6.34976 12.2169 6.01432 12.1151C5.63542 12 5.21799 12 4.38313 12H3.46659C2.71985 12 2.34648 12 2.06126 11.8547C1.81038 11.7268 1.60641 11.5229 1.47858 11.272C1.33325 10.9868 1.33325 10.6134 1.33325 9.86667V4.13333C1.33325 3.3866 1.33325 3.01323 1.47858 2.72801C1.60641 2.47713 1.81038 2.27316 2.06126 2.14532C2.34648 2 2.71985 2 3.46659 2H3.73325C5.22673 2 5.97346 2 6.54389 2.29065C7.04566 2.54631 7.45361 2.95426 7.70927 3.45603C7.99992 4.02646 7.99992 4.77319 7.99992 6.26667M7.99992 14V6.26667M7.99992 14L8.06662 13.8999C8.52972 13.2053 8.76126 12.858 9.06718 12.6065C9.33801 12.3839 9.65008 12.2169 9.98552 12.1151C10.3644 12 10.7818 12 11.6167 12H12.5333C13.28 12 13.6534 12 13.9386 11.8547C14.1895 11.7268 14.3934 11.5229 14.5213 11.272C14.6666 10.9868 14.6666 10.6134 14.6666 9.86667V4.13333C14.6666 3.3866 14.6666 3.01323 14.5213 2.72801C14.3934 2.47713 14.1895 2.27316 13.9386 2.14532C13.6534 2 13.28 2 12.5333 2H12.2666C10.7731 2 10.0264 2 9.45594 2.29065C8.95418 2.54631 8.54623 2.95426 8.29057 3.45603C7.99992 4.02646 7.99992 4.77319 7.99992 6.26667"
            stroke="#962EFF"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <defs>
          <clipPath id="clip0_37_865">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    format: (v) => v,
  },
  average_score: {
    name: "Average Score",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_37_873)">
          <path
            d="M14.6666 4.66667L9.42083 9.91243C9.15682 10.1764 9.02482 10.3084 8.8726 10.3579C8.7387 10.4014 8.59447 10.4014 8.46057 10.3579C8.30835 10.3084 8.17635 10.1764 7.91234 9.91242L6.0875 8.08758C5.82349 7.82357 5.69148 7.69157 5.53926 7.64211C5.40537 7.5986 5.26114 7.5986 5.12724 7.64211C4.97502 7.69157 4.84302 7.82357 4.579 8.08759L1.33325 11.3333M14.6666 4.66667H9.99992M14.6666 4.66667V9.33334"
            stroke="#44F87D"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <defs>
          <clipPath id="clip0_37_873">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    format: (v) => `${v.toFixed(1)}`,
  },
  practice_time_seconds: {
    name: "Total Practice Time",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.99992 3.99999V7.99999L10.6666 9.33333M14.6666 7.99999C14.6666 11.6819 11.6818 14.6667 7.99992 14.6667C4.31802 14.6667 1.33325 11.6819 1.33325 7.99999C1.33325 4.3181 4.31802 1.33333 7.99992 1.33333C11.6818 1.33333 14.6666 4.3181 14.6666 7.99999Z"
          stroke="#01ECFF"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    format: (v) => `${Math.floor(v / 3600)}h ${Math.floor((v % 3600) / 60)}m`,
  },
};

const QUICK_TIPS = [
  {
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.00008 9.99999L6.00008 7.99999M8.00008 9.99999C8.93131 9.64582 9.82467 9.19914 10.6667 8.66666M8.00008 9.99999V13.3333C8.00008 13.3333 10.0201 12.9667 10.6667 12C11.3867 10.92 10.6667 8.66666 10.6667 8.66666M6.00008 7.99999C6.35484 7.07961 6.80155 6.19737 7.33341 5.36666C8.1102 4.12465 9.19183 3.10202 10.4754 2.39605C11.759 1.69008 13.2019 1.32424 14.6667 1.33333C14.6667 3.14666 14.1467 6.33333 10.6667 8.66666M6.00008 7.99999H2.66675C2.66675 7.99999 3.03341 5.97999 4.00008 5.33333C5.08008 4.61333 7.33341 5.33333 7.33341 5.33333M3.00008 11C2.00008 11.84 1.66675 14.3333 1.66675 14.3333C1.66675 14.3333 4.16008 14 5.00008 13C5.47341 12.44 5.46675 11.58 4.94008 11.06C4.68095 10.8127 4.33961 10.6697 3.98157 10.6587C3.62352 10.6476 3.274 10.7692 3.00008 11Z"
          stroke="#962EFF"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Getting Started",
    subtitle: "Platform Overview & Navigation",
    duration: "3 min",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.99992 14L7.93322 13.8999C7.47012 13.2053 7.23857 12.858 6.93265 12.6065C6.66182 12.3839 6.34976 12.2169 6.01432 12.1151C5.63542 12 5.21799 12 4.38313 12H3.46659C2.71985 12 2.34648 12 2.06126 11.8547C1.81038 11.7268 1.60641 11.5229 1.47858 11.272C1.33325 10.9868 1.33325 10.6134 1.33325 9.86667V4.13333C1.33325 3.3866 1.33325 3.01323 1.47858 2.72801C1.60641 2.47713 1.81038 2.27316 2.06126 2.14532C2.34648 2 2.71985 2 3.46659 2H3.73325C5.22673 2 5.97346 2 6.54389 2.29065C7.04566 2.54631 7.45361 2.95426 7.70927 3.45603C7.99992 4.02646 7.99992 4.77319 7.99992 6.26667M7.99992 14V6.26667M7.99992 14L8.06662 13.8999C8.52972 13.2053 8.76126 12.858 9.06718 12.6065C9.33801 12.3839 9.65008 12.2169 9.98552 12.1151C10.3644 12 10.7818 12 11.6167 12H12.5333C13.28 12 13.6534 12 13.9386 11.8547C14.1895 11.7268 14.3934 11.5229 14.5213 11.272C14.6666 10.9868 14.6666 10.6134 14.6666 9.86667V4.13333C14.6666 3.3866 14.6666 3.01323 14.5213 2.72801C14.3934 2.47713 14.1895 2.27316 13.9386 2.14532C13.6534 2 13.28 2 12.5333 2H12.2666C10.7731 2 10.0264 2 9.45594 2.29065C8.95418 2.54631 8.54623 2.95426 8.29057 3.45603C7.99992 4.02646 7.99992 4.77319 7.99992 6.26667"
          stroke="#962EFF"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "How Lessons Work",
    subtitle: "Learn how to navigate lessons...",
    duration: "3 min",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.66659 14.6666V7.33331M1.33325 8.66665V13.3333C1.33325 14.0697 1.93021 14.6666 2.66659 14.6666H11.6174C12.6046 14.6666 13.4441 13.9464 13.5942 12.9708L14.3121 8.30409C14.4985 7.09258 13.5611 5.99998 12.3354 5.99998H9.99992C9.63173 5.99998 9.33325 5.7015 9.33325 5.33331V2.97721C9.33325 2.06931 8.59726 1.33331 7.68936 1.33331C7.47281 1.33331 7.27657 1.46084 7.18862 1.65873L4.84254 6.9374C4.73554 7.17816 4.4968 7.33331 4.23334 7.33331H2.66659C1.93021 7.33331 1.33325 7.93027 1.33325 8.66665Z"
          stroke="#962EFF"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Simulations & Feedback",
    subtitle: "See how feedback helps you r...",
    duration: "3 min",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.6666 14V12.6667C14.6666 11.4241 13.8167 10.38 12.6666 10.084M10.3333 2.19384C11.3105 2.58943 11.9999 3.54754 11.9999 4.66667C11.9999 5.78579 11.3105 6.7439 10.3333 7.13949M11.3333 14C11.3333 12.7575 11.3333 12.1362 11.1303 11.6462C10.8596 10.9928 10.3405 10.4736 9.68707 10.203C9.19702 10 8.57576 10 7.33325 10H5.33325C4.09074 10 3.46949 10 2.97943 10.203C2.32602 10.4736 1.80689 10.9928 1.53624 11.6462C1.33325 12.1362 1.33325 12.7575 1.33325 14M8.99992 4.66667C8.99992 6.13943 7.80601 7.33333 6.33325 7.33333C4.86049 7.33333 3.66659 6.13943 3.66659 4.66667C3.66659 3.19391 4.86049 2 6.33325 2C7.80601 2 8.99992 3.19391 8.99992 4.66667Z"
          stroke="#962EFF"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Meet Your Coach",
    subtitle: "How to use your on-demand c...",
    duration: "3 min",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
];

export default function DashboardHome() {
  const { brand, featureFlags } = React.useContext(BrandContext);
  const { loggedInUser, authToken } = useAuth();
  const { callAPI } = useAPI();
  const { isMobile } = useUI();
  const [activeVideo, setActiveVideo] = React.useState(null);

  const currentPath = window.location.pathname.toLowerCase();
  const is2FA = currentPath.includes("2fa");
  const shouldFetch = !!authToken && !is2FA;
  const navigate = useNavigate();

  React.useEffect(() => {
    let title;
    if (loggedInUser && loggedInUser.first_name) {
      title = `${loggedInUser.first_name}'s Dashboard - ${brand.name}`;
    } else {
      title = `${brand.name} Dashboard`;
    }
    document.title = title;
  }, [loggedInUser, brand.name]);

  const { data: metricData = {}, isLoading: isMetricDataLoading } = useQuery({
    queryKey: ["user_metrics", authToken],
    queryFn: () => callAPI(`user_metrics`, { token: authToken }),
    staleTime: 1000 * 60 * 5,
    enabled: shouldFetch,
  });

  const { data: pastSimData = {}, isLoading: isPastSimDataLoading } = useQuery({
    queryKey: ["dash_recent_sims", authToken],
    queryFn: () => callAPI(`simulation?page_size=3`, { token: authToken }),
    staleTime: 1000 * 60 * 5,
    enabled: shouldFetch,
  });

  const metrics = Object.entries(metricData).map(([key, value]) => ({
    name: METRIC_CONFIG[key]?.name ?? key,
    icon: METRIC_CONFIG[key]?.icon ?? "📊",
    value: value != null ? (METRIC_CONFIG[key]?.format(value) ?? value) : "—",
  }));

  return (
    <Container>
      {loggedInUser.first_name && (
        <PageTitle
          text={`Hey ${loggedInUser.first_name ? loggedInUser.first_name : ""}, welcome back! 👋`}
        >
          Keep up the great work - your next lesson awaits 📚
        </PageTitle>
      )}

      {isMetricDataLoading ? (
        <DashLoading text="Loading Metrics..." />
      ) : (
        <div className="metric-tiles">
          {metrics.map((metric, i) => (
            <Metrics metricData={metric} key={i} />
          ))}
        </div>
      )}

      <div className="dashboard-content">
        <div className="main-content">
          <HomeProgramCard />
          {featureFlags?.conversation_insights === true && !isMobile ? (
            <CIWidget />
          ) : null}
          {!isMobile && <PreviousResultsChart />}
        </div>
        <div className="sidebar">
          <SidebarWidget
            title="Quick Tips"
            iconAbove={
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_230_476)">
                  <path
                    d="M7.99992 1.33334V2.00001M1.99992 8.00001H1.33325M3.66659 3.66668L3.26652 3.26661M12.3333 3.66668L12.7334 3.26661M14.6666 8.00001H13.9999M6.66658 9.00001H9.33325M7.99992 9.00001V12.3333M10.3333 11.2493C11.3426 10.5232 11.9999 9.33836 11.9999 8.00001C11.9999 5.79087 10.2091 4.00001 7.99992 4.00001C5.79078 4.00001 3.99992 5.79087 3.99992 8.00001C3.99992 9.33836 4.65721 10.5232 5.66659 11.2493V12.5333C5.66659 13.2801 5.66659 13.6534 5.81191 13.9387C5.93974 14.1895 6.14371 14.3935 6.3946 14.5214C6.67981 14.6667 7.05318 14.6667 7.79992 14.6667H8.19992C8.94665 14.6667 9.32002 14.6667 9.60524 14.5214C9.85612 14.3935 10.0601 14.1895 10.1879 13.9387C10.3333 13.6534 10.3333 13.2801 10.3333 12.5333V11.2493Z"
                    stroke="#01ECFF"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_230_476">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
            iconAboveText="Getting Started"
          >
            <ul>
              {QUICK_TIPS.map((tip, i) => (
                <li key={i} style={{ position: "relative" }}>
                  <div className="tip-icon">{tip.icon}</div>
                  <div className="tip-info">
                    <div className="tip-title">{tip.title}</div>
                    <div className="tip-subtitle">{tip.subtitle}</div>
                  </div>
                  <div className="tip-meta">
                    {tip.duration}
                    <div className="play-btn">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.33325 3.32642C3.33325 2.67898 3.33325 2.35526 3.46825 2.17681C3.58585 2.02135 3.7656 1.92515 3.96018 1.91353C4.18354 1.9002 4.45289 2.07977 4.9916 2.4389L12.002 7.11248C12.4471 7.40923 12.6697 7.55761 12.7472 7.74462C12.815 7.90813 12.815 8.09188 12.7472 8.25538C12.6697 8.4424 12.4471 8.59077 12.002 8.88752L4.9916 13.5611C4.45289 13.9202 4.18354 14.0998 3.96018 14.0865C3.7656 14.0749 3.58585 13.9787 3.46825 13.8232C3.33325 13.6447 3.33325 13.321 3.33325 12.6736V3.32642Z"
                          stroke="#FEFEFE"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(8, 7, 28, 0.52)",
                      backdropFilter: "blur(1.5px)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "default",
                    }}
                  >
                    <span
                      style={{
                        color: "#A87EFF",
                        fontSize: "11px",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Video Coming Soon
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </SidebarWidget>
          {featureFlags?.conversation_insights === true && isMobile ? (
            <CIWidget />
          ) : null}
          <SidebarWidget
            title="Performance Spotlight"
            iconAbove={
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3334 13.3333V8.66667M8.00008 13.3333V6.66667M2.66675 13.3333L2.66675 10.6667M8.93787 3.35167L12.3835 4.64378M7.19926 3.60061L3.4669 6.39989M14.0405 4.29289C14.431 4.68342 14.431 5.31658 14.0405 5.70711C13.65 6.09763 13.0168 6.09763 12.6263 5.70711C12.2358 5.31658 12.2358 4.68342 12.6263 4.29289C13.0168 3.90237 13.65 3.90237 14.0405 4.29289ZM3.37386 6.29289C3.76438 6.68342 3.76438 7.31658 3.37386 7.70711C2.98333 8.09763 2.35017 8.09763 1.95964 7.70711C1.56912 7.31658 1.56912 6.68342 1.95964 6.29289C2.35017 5.90237 2.98333 5.90237 3.37386 6.29289ZM8.70719 2.29289C9.09771 2.68342 9.09771 3.31658 8.70719 3.70711C8.31666 4.09763 7.6835 4.09763 7.29297 3.70711C6.90245 3.31658 6.90245 2.68342 7.29297 2.29289C7.6835 1.90237 8.31666 1.90237 8.70719 2.29289Z"
                  stroke="#01ECFF"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            iconAboveText="Your Progress"
          >
            <p>
              Track your latest scores, revisit feedback, and see how your
              skills are improving over time.
            </p>
            <div className="previous-sims">
              {isPastSimDataLoading ? (
                <DashLoading text="Loading Previous Sims..." />
              ) : (
                <>
                  {pastSimData.data.map((sim) => (
                    <div
                      key={sim.id}
                      className="sim-card"
                      onClick={() => navigate(`/dashboard/feedback/${sim.id}`)}
                    >
                      <div className="sim-card__title">{sim.name}</div>
                      <div className="sim-card__category">
                        {sim.short_description.length >= 80
                          ? sim.short_description.slice(0, 80) + "..."
                          : sim.short_description}
                      </div>
                      <div className="sim-card__footer">
                        {sim.type !== "conversation_insight" ? (
                          <div className="sim-card__score">
                            <span className="score-label">Score</span>
                            <span className="score-value">
                              {sim.evaluation?.total_score?.value
                                ? (
                                    parseFloat(
                                      sim.evaluation.total_score?.value,
                                    ) / 10
                                  ).toFixed(1) + "/10"
                                : "N/A"}
                            </span>
                          </div>
                        ) : (
                          <div className="convo-insights-badge">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 11 11"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4.5 7L2.96237 8.55685C2.74789 8.77401 2.64065 8.88259 2.54848 8.89025C2.46851 8.89689 2.39021 8.86474 2.33798 8.80382C2.27778 8.7336 2.27778 8.58099 2.27778 8.27577V7.49581C2.27778 7.22198 2.05354 7.02385 1.7826 6.98416C1.12687 6.88811 0.611888 6.37313 0.515838 5.7174C0.5 5.60928 0.5 5.48026 0.5 5.22222V2.9C0.5 2.05992 0.5 1.63988 0.66349 1.31901C0.8073 1.03677 1.03677 0.8073 1.31901 0.66349C1.63988 0.5 2.05992 0.5 2.9 0.5H6.6C7.44008 0.5 7.86012 0.5 8.18099 0.66349C8.46323 0.8073 8.6927 1.03677 8.83651 1.31901C9 1.63988 9 2.05992 9 2.9V5M9 10.5L7.9118 9.74344C7.75883 9.63709 7.68234 9.58391 7.5991 9.54621C7.52522 9.51275 7.44755 9.48841 7.36779 9.47371C7.27792 9.45714 7.18477 9.45714 6.99846 9.45714H6.1C5.53995 9.45714 5.25992 9.45714 5.04601 9.34815C4.85785 9.25228 4.70487 9.0993 4.60899 8.91113C4.5 8.69722 4.5 8.4172 4.5 7.85714V6.6C4.5 6.03995 4.5 5.75992 4.60899 5.54601C4.70487 5.35785 4.85785 5.20487 5.04601 5.10899C5.25992 5 5.53995 5 6.1 5H8.9C9.46005 5 9.74008 5 9.95399 5.10899C10.1422 5.20487 10.2951 5.35785 10.391 5.54601C10.5 5.75992 10.5 6.03995 10.5 6.6V7.95714C10.5 8.42308 10.5 8.65606 10.4239 8.83983C10.3224 9.08485 10.1277 9.27953 9.88268 9.38102C9.69891 9.45714 9.46594 9.45714 9 9.45714V10.5Z"
                                stroke="#FEFEFE"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Conversation Insight
                          </div>
                        )}
                        <a className="sim-card__feedback">
                          View Feedback
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333"
                              stroke="#A87EFF"
                              strokeWidth="1.66667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </a>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <DefaultButton
              className="widget-cta-btn"
              onClick={() => navigate("/dashboard/simulations")}
            >
              View All Performance
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333"
                  stroke="#030039"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </DefaultButton>
          </SidebarWidget>
        </div>
      </div>
      {activeVideo && (
        <VideoModal
          url={activeVideo.videoUrl}
          title={activeVideo.title}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </Container>
  );
}
